import "./Checkout.css";

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../hooks/useCart";

//custom hooks
import { useAuthContext } from "../../hooks/useAuthContext";
import getAuthHeaders from "../../utils/getAuthHeaders";

export default function Checkout() {
  const navigate = useNavigate();
  const { cartTotal, itemCount, cart, fulfillment } = useCart();
  const { user } = useAuthContext();
  const [errorMessage, setErrorMessage] = useState(null);
  const [hydratedCart, setHydratedCart] = useState([]);
  const [isLoadingCart, setIsLoadingCart] = useState(true);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const handleCheckout = async () => {
    setIsCheckoutLoading(true);
    setErrorMessage(null);
    try {
      const updatedCart = hydratedCart.filter(
        (p) => p.quantityAvailable > 0 && p.inventoryStatus === "include"
      );
      const authHeader = await getAuthHeaders(user);
      const url =
        process.env.NODE_ENV === "development"
          ? `${process.env.REACT_APP_NETWORK}/api/checkout/create-coupon-checkout-session`
          : "/api/checkout/create-coupon-checkout-session";

      const response = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...authHeader },
        body: JSON.stringify({
          cart: updatedCart,
          fulfillment,
          taxRate: fulfillment?.taxRate || null,
          uid: user.uid,
        }),
      });
      const data = await response.json();
      if (!response.ok) {
        setErrorMessage(
          data?.error?.message || "Unable to open Stripe checkout right now."
        );
        return;
      }
      window.location.assign(data.url);
    } catch (err) {
      setErrorMessage("Unable to open Stripe checkout right now.");
    } finally {
      setIsCheckoutLoading(false);
    }
  };

  // Hydrate cart with full product details on checkout page load
  useEffect(() => {
    const hydrateCart = async () => {
      if (!user || !cart || cart.length === 0) {
        setHydratedCart([]);
        setIsLoadingCart(false);
        return;
      }

      try {
        setIsLoadingCart(true);
        const productIds = cart.map((item) => item.productId);

        const authHeader = await getAuthHeaders(user);

        const fetchUrl =
          process.env.NODE_ENV === "development"
            ? `${process.env.REACT_APP_NETWORK}/api/products/by-ids`
            : "/api/products/by-ids";

        const response = await fetch(fetchUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...authHeader,
          },
          body: JSON.stringify({
            productIds,
            cartItems: cart,
          }),
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch products: ${response.statusText}`);
        }

        const data = await response.json();
        setHydratedCart(data.products || []);
        setIsLoadingCart(false);
      } catch (error) {
        console.error("Error hydrating cart:", error);
        setIsLoadingCart(false);
      }
    };

    hydrateCart();
  }, [user, cart]);

  const handleCustomPickup = () => {
    return fulfillment.customPickup ? (
      <span>
        <b>• Custom Pick-up:</b> we will contact you to schedule a custom
        pick-up time
      </span>
    ) : (
      <span>
        <b>• Your pick-up is scheduled for</b> {fulfillment.fulfillmentDate}.
        We'll let you know if anything changes.
      </span>
    );
  };

  const CheckoutNotes = () => {
    return (
      <div className="checkout-notes">
        <h3>Important note!</h3>
        <ul>
          <li className="checkout-li">
            <p>
              <b>
                • Items that currently show in stock may become unavailable.{" "}
              </b>{" "}
              If an item becomes unavailable after you place your order, we will
              notify you and adjust your order total accordingly.
            </p>
          </li>
          {fulfillment?.type === "Pickup" ? (
            <li className="checkout-li">{handleCustomPickup()}</li>
          ) : (
            <></>
          )}
        </ul>
      </div>
    );
  };

  const CartItems = () => {
    return (
      <div className="order-summary">
        <h3>Order Summary</h3>
        {isLoadingCart ? (
          <p>Loading cart items...</p>
        ) : hydratedCart.length === 0 ? (
          <p>No items in cart</p>
        ) : (
          <div className="cart-items-list">
            {hydratedCart.map((item) => (
              <div key={item.productId} className="cart-item">
                <div className="cart-item-image">
                  {item.thumbnail && (
                    <img
                      src={item.thumbnailUrl || item.thumbnail}
                      alt={item.description}
                    />
                  )}
                </div>
                <div className="cart-item-details">
                  <p className="cart-item-description">{item.description}</p>
                  <p className="cart-item-size">{item.unitSize}</p>
                  <div className="cart-item-quantity-price">
                    <span>Qty: {item.quantity}</span>
                    <span className="cart-item-price">
                      ${((item.casePrice * item.quantity) / 100).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const CheckoutActions = () => {
    return (
      <div className="checkoutForm">
        <h3>Ready to complete your purchase?</h3>
        <div className="checkoutBox" style={{ marginBottom: "10px" }}>
          <p>
            <b>Subtotal</b> ({itemCount} {itemCount > 1 ? "items" : "item"})
          </p>
          <p>${Number.parseFloat(cartTotal / 100).toFixed(2)}</p>
        </div>
        <div className="checkoutBox" style={{ marginBottom: "2px" }}>
          <p>
            <b>Sales Tax</b>
          </p>
          <p>TBD</p>
        </div>
        <p className="checkout-tax-note">calculated in checkout</p>
        <div className="checkoutBox" style={{ marginBottom: "10px" }}>
          <p>
            <b>Est. Total</b>
          </p>
          <p>${Number.parseFloat(cartTotal / 100).toFixed(2)}</p>
        </div>
        <button
          className="apply-coupon-btn"
          onClick={handleCheckout}
          disabled={
            isCheckoutLoading || isLoadingCart || hydratedCart.length === 0
          }
        >
          {isCheckoutLoading ? "Redirecting..." : "CHECKOUT"}
        </button>
        {errorMessage && <p>{errorMessage}</p>}
      </div>
    );
  };

  return (
    <div className="checkout-container">
      <CheckoutNotes />
      <CartItems />
      <div class="checkout-sidebar">
        <CheckoutActions />
      </div>
    </div>
  );
}
